// The Booking list's derivation rules, as pure functions — no React, no CSS, no network. The list layout is
// the half of the Booking regression Ryan reported that is *provable*: every rule below comes from the decoded
// dev reference (`.design-specs/booking-page.txt`) and is pinned by `bookingRows.test.ts`, so parity survives a
// re-style. Nothing here touches money — a row carries no guarantee and a tour no total (the money-gated list
// DTO those need does not exist yet; see `.design-specs/booking-page.audit.md`).
import type { components } from "@cntrl/api-client";

type ShowSummary = components["schemas"]["ShowSummary"];
type TourDayDetail = components["schemas"]["TourDayDetail"];
type SharedShowDetail = components["schemas"]["SharedShowDetail"];

export type BookingTab = "confirmed" | "holds" | "past";

/** A show row inside the timeline (standalone or nested under a tour). */
export interface ShowItem {
  kind: "show";
  key: string;
  date: string;
  show: ShowSummary;
  /**
   * A show a FRIEND BAND shared with us that we ACCEPTED (ART-165). It renders as an ordinary row in the
   * Upcoming Shows list — Ryan: *"Should show up in your shows box not separate … should look just like a
   * normal show"* — but opens the read-only shared detail rather than the band's own show editor/preview,
   * because the guest has no `/shows/{id}` access (only `/shared-shows/{id}`).
   */
  shared?: boolean;
  /**
   * ART-240: the off/travel days ATTRIBUTED to this show — a day linked directly to it, or a run-linked day
   * whose "next show" is this one (the PO's attribution rule, resolved client-side here so it is money-free).
   * Date-sorted. Populated only for LOOSE shows (a tour absorbs its own days). A show with days renders as a
   * closable RUN CONTAINER (show hero + its day rows); an empty/absent list renders as a bare hero.
   */
  days?: OffItem[];
}

/**
 * The tab an accepted shared show belongs on, from the HOST show's own status (ART-165, Ryan: *"it should
 * show in the holds tab if it's still a hold at its source"*). A shared date is bucketed exactly as the band's
 * own shows are: a confirmed/settled host show reads on Confirmed, a hold reads on Holds. `showStatus` is the
 * host lifecycle the matrix now carries; absent (an older payload) falls back to `confirmed`, the pre-ART-165
 * behaviour, so a stale cache never drops a shared row off both tabs.
 */
function sharedShowStatus(detail: SharedShowDetail): string {
  return detail.showStatus === "hold" ? "hold" : "confirmed";
}

/** The upcoming tab a shared show (or a pending invite) belongs on, from the HOST show's status — a hold at
 *  its source lands on Holds, everything else on Confirmed. Shared by the timeline fold and the pending-invite
 *  group so an invite and the show it becomes bucket the same way (ART-165). */
export function sharedShowTab(detail: SharedShowDetail): Extract<BookingTab, "confirmed" | "holds"> {
  return detail.showStatus === "hold" ? "holds" : "confirmed";
}

/**
 * Adapt a guest's ACCEPTED shared show (the read-only field matrix from `GET /shared-shows`) into the
 * `ShowSummary` the normal row renders from, so a shared date reads exactly like one of the band's own on the
 * Booking list. What the matrix does not carry simply renders as its absent state: no member faces
 * (`memberPersonIds` empty), no money (the money-gated list is keyed by the band's OWN show ids and never
 * holds this host show id, so the facts strip's money cluster stays dark). The poster renders from the
 * guest-reachable shared-flyer route (`FlyerPanel`, ART-165), so it no longer falls back to a caption. `status`
 * is the HOST show's own status (hold | confirmed) via `sharedShowStatus`, so the row wears the right badge AND
 * lands on the right tab — a shared hold on Holds, a shared confirmed date on Confirmed.
 *
 * The bill lights the AC mark per act from `friendBandId` — the flag the DTO always carried and this adapter
 * used to discard, so a shared show's whole bill showed no mark on the guest's own Booking page (ART-165).
 */
export function sharedShowToSummary(detail: SharedShowDetail): ShowSummary {
  const bill = [...(detail.lineup ?? [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((slot) => ({ artistName: slot.artistName ?? null, onArtistCntrl: slot.friendBandId != null }));
  return {
    showId: detail.showId,
    kind: detail.kind ?? null,
    status: sharedShowStatus(detail),
    date: detail.date,
    venueName: detail.venueName ?? null,
    city: detail.city ?? null,
    state: detail.state ?? null,
    country: detail.country ?? null,
    capacity: detail.capacity ?? null,
    // ART-300 — the GUEST band's OWN run / tour link for this date (never the host's), so the guest's Booking
    // groups it with its own run or tour exactly like one of its own shows.
    tourId: detail.tourId ?? null,
    runId: detail.runId ?? null,
    flyerMediaId: detail.flyerMediaId ?? null,
    doorsTime: detail.doorsTime ?? null,
    ticketUrl: detail.ticketUrl ?? null,
    bill,
    memberPersonIds: [],
  };
}

/**
 * The shared shows a guest band actually PLAYS from `fromIso` on, as rows: an ACCEPTED share (an invite is not a
 * date you play) on a CONFIRMED host date (a host hold is not day-of-show material). The one rule both the DOS
 * tab and the mobile Upcoming card read (ART-249/251), so the two cannot disagree about which guest dates count.
 */
export function upcomingSharedShows(details: readonly SharedShowDetail[] | undefined, fromIso: string): ShowSummary[] {
  return (details ?? [])
    .filter((d) => d.status === "accepted" && d.showStatus === "confirmed" && (d.date ?? "") >= fromIso)
    .map(sharedShowToSummary);
}

/** A recessed off/travel row. No flyer, no guarantee, no View — clicking it edits the day. */
export interface OffItem {
  kind: "off";
  key: string;
  date: string;
  day: TourDayDetail;
}

/** A collapsible tour group: its shows and off days interleaved by date. */
export interface TourItem {
  kind: "tour";
  key: string;
  /** The group's earliest date — what it sorts on. */
  date: string;
  /** The group's latest date. */
  endDate: string;
  name: string;
  children: (ShowItem | OffItem)[];
  /** Show dates only. Off days are counted SEPARATELY and never absorbed into this tally. */
  showCount: number;
  offCount: number;
  confirmedCount: number;
}

export type TimelineItem = ShowItem | OffItem | TourItem;

// ---- dates & times (display-only; nothing here is ever written back) -------------------------------------

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * **The module's one "today" authority** — device-local `yyyy-mm-dd`, never UTC.
 *
 * `new Date().toISOString().slice(0,10)` rolls the day over at 19:00 CDT / 17:00 PDT, so on the evening of a
 * show day a US band's show jumps to the Past tab, the server filter drops it from `from=`, and the editor
 * header reads `1 day ago` while the band is loading in. Every other date in this file is parsed LOCAL
 * (`parseIsoDate`), so a UTC "today" compared against them is a straight one-day skew.
 *
 * `offsetDays` shifts by whole LOCAL days (`setDate` normalises months/years and is DST-safe for a date-only
 * value). Same construction as `OpsCalendarScreen`, which now imports this rather than keeping its own copy.
 */
export function todayIso(offsetDays = 0): string {
  const d = new Date();
  if (offsetDays !== 0) d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Parse a `yyyy-mm-dd` as a LOCAL date. `new Date("2026-08-01")` is UTC midnight, which renders as Jul 31
 * west of Greenwich — that one-day slip is why every date here goes through this. */
export function parseIsoDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `Fri` — the top half of a row's 52px date block. */
export function dowOf(iso: string): string {
  const d = parseIsoDate(iso);
  return d ? d.toLocaleDateString(undefined, { weekday: "short" }) : "";
}

/** `12` — the bottom half of a row's date block. */
export function dayOf(iso: string): string {
  const d = parseIsoDate(iso);
  return d ? String(d.getDate()) : "";
}

/** `Jun 4` — the compact form a tour range is built from. */
export function shortDate(iso: string): string {
  const d = parseIsoDate(iso);
  return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
}

/** `Fri Nov 20` — a run container's header names its show by weekday + short month/day. */
export function dowShortDate(iso: string): string {
  const d = parseIsoDate(iso);
  if (!d) return "";
  const dow = d.toLocaleDateString(undefined, { weekday: "short" });
  const md = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${dow} ${md}`;
}

/**
 * A run container's date span for its header — `Nov 19 – 20 · 2 days`. The range runs from the earliest to the
 * latest DISTINCT calendar date across the show and its attributed off/travel days, collapsing the month when
 * both ends share it; `dayCount` is that distinct-date count. A show with no days never reaches here (it renders
 * as a bare hero), so the range always spans at least the show day plus one.
 */
export function containerSpan(item: ShowItem): { range: string; dayCount: number } {
  const dates = Array.from(
    new Set([item.date, ...(item.days ?? []).map((d) => d.date)].filter(Boolean)),
  ).sort();
  const start = dates[0] ?? item.date;
  const end = dates[dates.length - 1] ?? item.date;
  const a = parseIsoDate(start);
  const b = parseIsoDate(end);
  let range: string;
  if (!a || !b || start === end) {
    range = shortDate(start);
  } else if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    range = `${shortDate(start)} – ${b.getDate()}`;
  } else {
    range = `${shortDate(start)} – ${shortDate(end)}`;
  }
  return { range, dayCount: dates.length };
}

/** `Friday, July 3, 2026` — the show editor's header line. */
export function longDate(iso: string): string {
  const d = parseIsoDate(iso);
  return d
    ? d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "";
}

/** `Jun 4 - Jun 9`, collapsing to one date when the tour is a single day. */
export function dateRange(from: string, to: string): string {
  const a = shortDate(from);
  const b = shortDate(to);
  if (!a) return b;
  if (!b || a === b) return a;
  return `${a} - ${b}`;
}

/**
 * `TODAY` on the day, `T-9 days` before it, `9 days ago` after. Whole days apart in LOCAL time, so a show
 * tonight never reads `T-0 days`.
 */
export function tMinus(iso: string, todayIso: string): string {
  const target = parseIsoDate(iso);
  const today = parseIsoDate(todayIso);
  if (!target || !today) return "";
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return "TODAY";
  const n = Math.abs(days);
  const unit = n === 1 ? "day" : "days";
  return days > 0 ? `T-${n} ${unit}` : `${n} ${unit} ago`;
}

/** Times always render 12-hour. Display-only — the wire value (`HH:mm:ss`) is never replaced by this. */
export function formatTime12(value: string | null | undefined): string | null {
  const m = /^(\d{1,2}):(\d{2})/.exec((value ?? "").trim());
  if (!m) return null;
  const h24 = Number(m[1]);
  if (!Number.isFinite(h24) || h24 > 23) return null;
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${m[2]} ${suffix}`;
}

/** The route line on an off/travel row: `Nashville, TN → Denver, CO`, or the single city, or null. */
export function offRoute(day: TourDayDetail): { from: string; to: string } | { city: string } | null {
  const from = (day.fromCity ?? "").trim();
  const to = (day.toCity ?? "").trim();
  if (from || to) return { from, to };
  const city = (day.city ?? "").trim();
  return city ? { city } : null;
}

/** `Rolls 9:00 AM` when the day carries a departure, `No travel` when it does not. */
export function offDeparture(day: TourDayDetail): string {
  const t = formatTime12(day.departTime);
  return t ? `Rolls ${t}` : "No travel";
}

// ---- tab predicates -------------------------------------------------------------------------------------

/** A date strictly before today is history. ISO dates compare lexicographically, so no parsing is needed. */
export function isPastDate(date: string | null | undefined, todayIso: string): boolean {
  return !!date && date < todayIso;
}

export function isConfirmed(show: ShowSummary): boolean {
  return show.status === "confirmed" || show.status === "settled";
}

/**
 * Confirmed = `!isPast && confirmed` · Holds = `!isPast && !confirmed` · Past = `isPast`.
 *
 * **A past unconfirmed show lands in Past, never Holds** — holds are future-only. The server already filters
 * each tab, so this is the client-side belt to that braces: a stale cache page or a show that rolls over at
 * midnight must not appear under a tab it no longer belongs to.
 */
export function matchesTab(show: ShowSummary, tab: BookingTab, todayIso: string): boolean {
  const past = isPastDate(show.date, todayIso);
  if (tab === "past") return past;
  if (past) return false;
  return tab === "confirmed" ? isConfirmed(show) : !isConfirmed(show);
}

/** Optimistic delete: a removed row disappears immediately and the reload confirms it. */
export function withoutHidden<T>(
  rows: readonly T[],
  hidden: ReadonlySet<string>,
  idOf: (row: T) => string | null | undefined,
): T[] {
  if (hidden.size === 0) return [...rows];
  return rows.filter((row) => {
    const id = idOf(row);
    return !id || !hidden.has(id);
  });
}

// ---- the timeline ---------------------------------------------------------------------------------------

export interface TimelineInput {
  shows: readonly ShowSummary[];
  /** Off/travel days from `GET /tour-days`. Pass `[]` when the surface is unavailable. */
  tourDays?: readonly TourDayDetail[];
  /** `tourId → display name`, from `useTours()`. */
  tourNames?: ReadonlyMap<string, string>;
  tab: BookingTab;
  /** `yyyy-mm-dd`. Injected rather than read from the clock so the rules are testable. */
  todayIso: string;
  /** Optimistically deleted show ids. */
  hiddenShows?: ReadonlySet<string>;
  /**
   * The band's ACCEPTED shared shows (`GET /shared-shows`, accepted only) — folded into the timeline as
   * ordinary rows (ART-165), bucketed by `matchesTab` exactly as the band's own shows: onto Confirmed or Holds
   * by the HOST show's status (`showStatus`) while upcoming, and onto PAST once the date has passed (a show the
   * guest opened is part of their own history too). Pass on EVERY tab — the fold self-buckets by date/status.
   */
  sharedShows?: readonly SharedShowDetail[];
}

/**
 * The one merged, date-sorted timeline the shows card renders: tour groups, loose shows and loose off days
 * in one list (Past newest-first, the upcoming tabs soonest-first).
 *
 * Off-day placement, verbatim from the spec:
 * - a day bound to a tour follows that tour onto **whichever tab the tour appears on** — no tab gate;
 * - a **standalone** day renders on **Confirmed only**;
 * - an **orphaned** day (its tour has no visible show on this tab) falls back to a standalone row, and so
 *   inherits the Confirmed-only gate rather than vanishing.
 *
 * Tours group by `tourId`; the spec's "trimmed, case-insensitive tour NAME, first-appearance order" is the
 * same rule expressed against legacy's free-text `tourName` field, so two tours sharing a name merge here too.
 */
export function buildTimeline({
  shows,
  tourDays = [],
  tourNames,
  tab,
  todayIso,
  hiddenShows,
  sharedShows = [],
}: TimelineInput): TimelineItem[] {
  const visible = withoutHidden(shows, hiddenShows ?? new Set<string>(), (s) => s.showId).filter((s) =>
    matchesTab(s, tab, todayIso),
  );

  const groups: TourItem[] = [];
  /** groupKey → group, where the key is the normalized tour NAME when we can resolve one. */
  const byKey = new Map<string, TourItem>();
  /** tourId → groupKey, so an off day finds the same bucket its shows landed in. */
  const keyByTourId = new Map<string, string>();
  const loose: TimelineItem[] = [];
  /** Every visible show item by id — a day linked to a specific show finds its target here whether the show
   *  is loose or a tour child. */
  const showItemById = new Map<string, ShowItem>();
  /** showId → its tour, so a day attributed to a tour child lands IN the tour rather than in the show's own
   *  `days` (tour children keep the existing interleaved layout; only loose shows become run containers). */
  const tourOfShowId = new Map<string, TourItem>();
  /** runId → the run's LOOSE shows (a run and a tour are mutually exclusive on a show), sorted ascending after
   *  the show loop — the substrate for the run-day attribution rule. */
  const runShows = new Map<string, ShowItem[]>();

  // Accepted shared shows join the SAME loop as the band's own, bucketed by `matchesTab` exactly as they are: a
  // confirmed host date on Confirmed, a hold on Holds, and a date that has PASSED on Past — a show the guest
  // OPENED belongs in their own Past list once it happens (ART-165 + the guest-Past fix, Ryan: an accepted share
  // that has passed was silently dropped, so an opener's played shows never reached their Past tab). A shared
  // date is a one-off row UNLESS the guest linked it into its OWN run or tour (ART-300, the summary carries the
  // guest's link, never the host's) — then it groups there like one of its own shows and takes the run's
  // attributed days. Safe on Past because a shared row is money-free on every tab — the money-gated list is keyed
  // by the band's OWN show ids and never holds this host show id, so its money cluster stays dark exactly as on
  // Confirmed — and the Past accordion is itself money-free (`groupByYear`).
  const entries: { show: ShowSummary; shared: boolean }[] = visible.map((show) => ({ show, shared: false }));
  for (const detail of sharedShows) {
    if (!detail.showId || !detail.date) continue;
    const summary = sharedShowToSummary(detail);
    if (matchesTab(summary, tab, todayIso)) entries.push({ show: summary, shared: true });
  }

  for (const { show, shared } of entries) {
    const showId = show.showId;
    if (!showId || !show.date) continue;
    const item: ShowItem = shared
      ? { kind: "show", key: `shared:${showId}`, date: show.date, show, shared: true }
      : { kind: "show", key: `show:${showId}`, date: show.date, show };
    showItemById.set(showId, item);

    if (!show.tourId) {
      item.days = [];
      loose.push(item);
      if (show.runId) {
        const rs = runShows.get(show.runId) ?? [];
        rs.push(item);
        runShows.set(show.runId, rs);
      }
      continue;
    }
    const name = (tourNames?.get(show.tourId) ?? "").trim();
    // Group by the normalized name so two tours called "Spring 2026" read as one, exactly as the free-text
    // legacy field did; an unresolved name (tours still loading) falls back to the id so rows never re-flow.
    const key = name ? `name:${name.toLowerCase()}` : `id:${show.tourId}`;
    keyByTourId.set(show.tourId, key);

    let group = byKey.get(key);
    if (!group) {
      group = {
        kind: "tour",
        key: `tour:${key}`,
        date: show.date,
        endDate: show.date,
        name: name || "Tour",
        children: [],
        showCount: 0,
        offCount: 0,
        confirmedCount: 0,
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.children.push(item);
    group.showCount += 1;
    if (isConfirmed(show)) group.confirmedCount += 1;
    tourOfShowId.set(showId, group);
  }

  for (const rs of runShows.values()) rs.sort(byDateAsc);

  // The run-day attribution rule (PO, ART-240): a day linked to a SHOW counts toward that show; a day linked to
  // a RUN counts toward the run's NEXT show (the earliest run show dated on or after the day), or the LAST show
  // when the day is after them all. Client-side over THIS tab's shows only — a target on another tab is not in
  // the window, so the day falls back to a loose row (the same degradation the "Linked to" line already makes).
  const attributedShow = (day: TourDayDetail): ShowItem | undefined => {
    if (day.showId) return showItemById.get(day.showId);
    if (day.runId) {
      const rs = runShows.get(day.runId);
      if (!rs || rs.length === 0) return undefined;
      return rs.find((it) => it.date >= (day.date ?? "")) ?? rs[rs.length - 1];
    }
    return undefined;
  };

  for (const day of tourDays) {
    const dayId = day.tourDayId;
    if (!dayId || !day.date) continue;
    const item: OffItem = { kind: "off", key: `off:${dayId}`, date: day.date, day };

    // ART-240 link (show or run) wins over the legacy tour absorption below.
    const target = attributedShow(day);
    if (target) {
      const tour = tourOfShowId.get(target.show.showId!);
      if (tour) {
        tour.children.push(item);
        tour.offCount += 1;
      } else {
        (target.days ??= []).push(item);
      }
      continue;
    }

    // Legacy tour-bound day (carries `tourId`, not a show/run link).
    const groupKey = day.tourId ? keyByTourId.get(day.tourId) : undefined;
    const group = groupKey ? byKey.get(groupKey) : undefined;
    if (group) {
      group.children.push(item);
      group.offCount += 1;
      continue;
    }
    // Standalone or orphaned — a loose row, and loose off days are a Confirmed-tab affordance only.
    if (tab === "confirmed") loose.push(item);
  }

  // Days attributed to a loose show sort ascending inside its container (the hero interleaves by date).
  for (const item of showItemById.values()) item.days?.sort(byDateAsc);

  for (const group of groups) {
    group.children.sort(byDateAsc);
    group.date = group.children[0]?.date ?? group.date;
    group.endDate = group.children[group.children.length - 1]?.date ?? group.date;
  }

  const timeline = [...groups, ...loose];
  timeline.sort(tab === "past" ? byDateDesc : byDateAsc);
  return timeline;
}

/** One year of the Past tab's accordion. Money-free: the reference's `· P&L +$X` needs the money-gated list
 * DTO, but the grouping, the year label and the `N shows` tally do not. */
export interface YearGroup {
  /** `2026` — the disclosure's label and its key. */
  year: string;
  items: TimelineItem[];
  /** SHOW dates in this year, tour children included; off/travel days are never counted as shows. */
  showCount: number;
}

/**
 * The Past tab's year accordion — `2026 · 3 shows`, newest year first.
 *
 * Spec: *"Past years accordion: newest year first, newest year open. Rows inside a year sort newest-first;
 * rows inside a tour are reversed again so the tour reads chronologically."* The caller feeds this the
 * already-sorted `past` timeline (newest-first, tour children ascending), so this only buckets — it never
 * re-sorts, and the two orders above are preserved by construction.
 *
 * A tour buckets by its EARLIEST date (`TourItem.date`), so a tour that straddles New Year renders whole
 * under the year it started rather than being split across two disclosures.
 */
export function groupByYear(items: readonly TimelineItem[]): YearGroup[] {
  const groups: YearGroup[] = [];
  const byYear = new Map<string, YearGroup>();
  for (const item of items) {
    const year = (item.date ?? "").slice(0, 4);
    if (year.length !== 4) continue;
    let group = byYear.get(year);
    if (!group) {
      group = { year, items: [], showCount: 0 };
      byYear.set(year, group);
      groups.push(group);
    }
    group.items.push(item);
    group.showCount += item.kind === "tour" ? item.showCount : item.kind === "show" ? 1 : 0;
  }
  // Newest year first, whatever order the caller's timeline arrived in.
  groups.sort((a, b) => (a.year < b.year ? 1 : a.year > b.year ? -1 : 0));
  return groups;
}

/** `3 shows` — the money-FREE half of the reference's `{N} shows · P&L +$X` year header. */
export function yearSummary(group: YearGroup): string {
  return `${group.showCount} ${group.showCount === 1 ? "show" : "shows"}`;
}

function byDateAsc(a: { date: string }, b: { date: string }): number {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
}

function byDateDesc(a: { date: string }, b: { date: string }): number {
  return -byDateAsc(a, b);
}

/**
 * `Jun 4 - Jun 9 · 3 dates · 1 off day · 2 confirmed`.
 *
 * The `· N confirmed` segment renders only when some show in the group is NOT confirmed, which makes it a
 * Holds-tab affordance by construction (on Confirmed every child is confirmed).
 */
export function tourSummary(group: TourItem): string {
  const parts = [dateRange(group.date, group.endDate)];
  parts.push(`${group.showCount} ${group.showCount === 1 ? "date" : "dates"}`);
  if (group.offCount > 0) parts.push(`${group.offCount} off ${group.offCount === 1 ? "day" : "days"}`);
  if (group.confirmedCount < group.showCount) parts.push(`${group.confirmedCount} confirmed`);
  return parts.join(" · ");
}

/**
 * A crew member's name as a show CHIP wears it — `Jane D`, first name plus the last name's initial
 * (PO, ART-92: *"Band member pill should just be Photo, First Name & First initial of last name"*).
 *
 * A chip row is scanned, not read: on a show with eight members the full names wrap to three lines and the
 * faces stop lining up, which is the state that was reported. The shortening is display-only — every other
 * surface (the profile dialog, the editor's picker, the invite list) keeps the whole name.
 *
 * Three cases the naive `split(" ")` gets wrong, all of them real rows in this app:
 *  • ONE word (a mononym, and the `"Invited member"` placeholder for a person who no longer holds a seat) —
 *    returned whole. `Invited` alone names nobody.
 *  • THREE-plus words — the initial comes from the LAST part, so `Ana Lucia Ramos` is `Ana R`, not `Ana L`.
 *  • An artist/owner account, whose person record IS the band name (`ArtistAccountsAreTheBand`) and is
 *    routinely two words that are not a first and last name. Nothing here can tell those apart, which is
 *    why the initial carries NO full stop: `Silver M` reads as a shortening, where `Silver M.` asserts a
 *    surname the row may not have.
 */
export function shortPersonName(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? "";
  const initial = parts[parts.length - 1]![0]!.toUpperCase();
  return `${parts[0]} ${initial}`;
}
